/**
 * Frontend logging service: captures console + global errors, batches, and uploads to S3.
 * - Overrides console.error, console.warn; provides logger.log
 * - Listens for window.onerror and window.onunhandledrejection
 * - Batches entries (timestamp, level, message, stack, currentRoute, userId)
 * - Flushes every 30s, at 50 entries, or on beforeunload
 * - Uploads to S3 via pre-signed URL or AWS SDK (env-configured)
 */

import { uploadLogsToS3, isS3LoggingConfigured } from './s3Upload';

const FLUSH_INTERVAL_MS = 30 * 1000;
const FLUSH_THRESHOLD = 50;
const LOG_FORMAT = 'txt'; // or 'json'

let sessionId;
function getSessionId() {
  if (!sessionId) {
    try {
      sessionId = sessionStorage.getItem('log_session_id');
      if (!sessionId) {
        sessionId = `session_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
        sessionStorage.setItem('log_session_id', sessionId);
      }
    } catch {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    }
  }
  return sessionId;
}

const context = { currentRoute: null, userId: null };
export function setLogContext(updates) {
  if (updates.currentRoute !== undefined) context.currentRoute = updates.currentRoute;
  if (updates.userId !== undefined) context.userId = updates.userId;
}

function getCurrentRoute() {
  if (context.currentRoute) return context.currentRoute;
  if (typeof window !== 'undefined' && window.location) return window.location.pathname || '';
  return '';
}

function createEntry(level, message, stack = null) {
  return {
    timestamp: new Date().toISOString(),
    level,
    message: typeof message === 'string' ? message : safeStringify(message),
    stack: stack || undefined,
    currentRoute: getCurrentRoute(),
    userId: context.userId ?? undefined,
  };
}

function safeStringify(value) {
  try {
    if (value instanceof Error) return value.message;
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
  } catch {
    return String(value);
  }
}

const queue = [];
let flushTimer = null;
let isFlushing = false;

function enqueue(entry) {
  queue.push(entry);
  if (queue.length >= FLUSH_THRESHOLD) scheduleFlush();
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, 0);
}

function serializeBatch(entries) {
  if (LOG_FORMAT === 'json') {
    return JSON.stringify(entries, null, 2);
  }
  return entries
    .map(
      (e) =>
        `[${e.timestamp}] ${e.level.toUpperCase()} ${e.message}` +
        (e.stack ? `\n  ${e.stack}` : '') +
        (e.currentRoute ? ` route=${e.currentRoute}` : '') +
        (e.userId ? ` userId=${e.userId}` : '')
    )
    .join('\n');
}

export async function flush() {
  if (isFlushing || queue.length === 0) return;
  isFlushing = true;
  const batch = queue.splice(0, queue.length);
  isFlushing = false;

  if (!isS3LoggingConfigured()) {
    if (process.env.NODE_ENV === 'development') {
      console.debug('[Logger] S3 not configured, skipping upload. Batch size:', batch.length);
    }
    return;
  }

  try {
    const body = serializeBatch(batch);
    await uploadLogsToS3(getSessionId(), body, LOG_FORMAT);
  } catch (err) {
    try {
      const origConsole = console.__loggerOriginal;
      if (origConsole && origConsole.error) origConsole.error('[Logger] Upload failed', err);
    } catch (_) {}
  }
}

const originalConsole = {
  log: null,
  warn: null,
  error: null,
};

function installConsoleOverrides() {
  if (originalConsole.error) return; // already installed
  originalConsole.log = console.log.bind(console);
  originalConsole.warn = console.warn.bind(console);
  originalConsole.error = console.error.bind(console);
  console.__loggerOriginal = originalConsole;

  console.error = function (...args) {
    const message = args.map(safeStringify).join(' ');
    const err = args.find((a) => a instanceof Error);
    enqueue(createEntry('error', message, err ? err.stack : null));
    originalConsole.error.apply(console, args);
  };

  console.warn = function (...args) {
    const message = args.map(safeStringify).join(' ');
    enqueue(createEntry('warn', message));
    originalConsole.warn.apply(console, args);
  };

}

function installGlobalErrorHandlers() {
  window.onerror = function (message, source, lineno, colno, error) {
    const stack = error && error.stack ? error.stack : `${source}:${lineno}:${colno}`;
    enqueue(createEntry('error', String(message), stack));
    return false; // allow default handling
  };

  window.onunhandledrejection = function (event) {
    const message =
      event.reason instanceof Error ? event.reason.message : safeStringify(event.reason);
    const stack = event.reason instanceof Error ? event.reason.stack : undefined;
    enqueue(createEntry('error', `Unhandled rejection: ${message}`, stack));
  };
}

function startFlushTimer() {
  setInterval(() => {
    if (queue.length > 0) scheduleFlush();
  }, FLUSH_INTERVAL_MS);
}

function installBeforeUnload() {
  window.addEventListener('beforeunload', () => {
    if (queue.length > 0) {
      flush();
    }
  });
  // Note: flush() is async; browser may not wait. For critical last-second logs,
  // consider a backend endpoint that accepts POST and forwards to S3, then sendBeacon to it here.
}

/**
 * Initialize the logging service. Call once at app startup (e.g. in index.js).
 */
export function initLogger() {
  installConsoleOverrides();
  installGlobalErrorHandlers();
  startFlushTimer();
  installBeforeUnload();
  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    window.logger = defaultExport;
  }
}

/** Custom log method: use logger.log() to capture and batch messages for S3 upload. */
function log(...args) {
  const message = args.map(safeStringify).join(' ');
  enqueue(createEntry('log', message));
  if (originalConsole.log) originalConsole.log.apply(console, args);
  else console.log(...args);
}

const defaultExport = {
  init: initLogger,
  setContext: setLogContext,
  flush,
  log,
};
export default defaultExport;
